import { toEnglishNumbers } from './numbers';

export interface JalaliDateParts {
  jy: number;
  jm: number;
  jd: number;
}

export interface GregorianDateParts {
  gy: number;
  gm: number;
  gd: number;
}

function div(a: number, b: number): number { return Math.trunc(a / b); }
function mod(a: number, b: number): number { return a - Math.trunc(a / b) * b; }

function jalCal(jy: number, withoutLeap = false): { leap: number; gy: number; march: number } {
  const breaks = [-61, 9, 38, 199, 426, 686, 756, 818, 1111, 1181, 1210, 1635, 2060, 2097, 2192, 2262, 2324, 2394, 2456, 3178];
  const bl = breaks.length;
  const gy = jy + 621;
  let leapJ = -14;
  let jp = breaks[0] ?? -61;
  let jump = 0;

  if (jy < jp || jy >= (breaks[bl - 1] ?? 3178)) throw new Error(`Invalid Jalali year ${jy}`);

  for (let index = 1; index < bl; index += 1) {
    const jm = breaks[index] ?? 3178;
    jump = jm - jp;
    if (jy < jm) break;
    leapJ += div(jump, 33) * 8 + div(mod(jump, 33), 4);
    jp = jm;
  }

  let n = jy - jp;
  leapJ += div(n, 33) * 8 + div(mod(n, 33) + 3, 4);
  if (mod(jump, 33) === 4 && jump - n === 4) leapJ += 1;

  const leapG = div(gy, 4) - div((div(gy, 100) + 1) * 3, 4) - 150;
  const march = 20 + leapJ - leapG;
  if (withoutLeap) return { leap: 0, gy, march };

  if (jump - n < 6) n = n - jump + div(jump + 4, 33) * 33;
  let leap = mod(mod(n + 1, 33) - 1, 4);
  if (leap === -1) leap = 4;
  return { leap, gy, march };
}

function g2d(gy: number, gm: number, gd: number): number {
  let d = div((gy + div(gm - 8, 6) + 100100) * 1461, 4)
    + div(153 * mod(gm + 9, 12) + 2, 5)
    + gd - 34840408;
  d = d - div(div(gy + 100100 + div(gm - 8, 6), 100) * 3, 4) + 752;
  return d;
}

function d2g(jdn: number): GregorianDateParts {
  let j = 4 * jdn + 139361631;
  j += div(div(4 * jdn + 183187720, 146097) * 3, 4) * 4 - 3908;
  const i = div(mod(j, 1461), 4) * 5 + 308;
  const gd = div(mod(i, 153), 5) + 1;
  const gm = mod(div(i, 153), 12) + 1;
  const gy = div(j, 1461) - 100100 + div(8 - gm, 6);
  return { gy, gm, gd };
}

function j2d(jy: number, jm: number, jd: number): number {
  const r = jalCal(jy, true);
  return g2d(r.gy, 3, r.march) + (jm - 1) * 31 - div(jm, 7) * (jm - 7) + jd - 1;
}

function d2j(jdn: number): JalaliDateParts {
  const g = d2g(jdn);
  let jy = g.gy - 621;
  const r = jalCal(jy, false);
  const jdn1f = g2d(g.gy, 3, r.march);
  let k = jdn - jdn1f;
  let jm: number;
  let jd: number;

  if (k >= 0) {
    if (k <= 185) {
      jm = 1 + div(k, 31);
      jd = mod(k, 31) + 1;
      return { jy, jm, jd };
    }
    k -= 186;
  } else {
    jy -= 1;
    k += 179;
    if (r.leap === 1) k += 1;
  }
  jm = 7 + div(k, 30);
  jd = mod(k, 30) + 1;
  return { jy, jm, jd };
}

export function toJalali(gy: number, gm: number, gd: number): JalaliDateParts {
  return d2j(g2d(gy, gm, gd));
}

export function toGregorian(jy: number, jm: number, jd: number): GregorianDateParts {
  return d2g(j2d(jy, jm, jd));
}

export function jalaliMonthLength(jy: number, jm: number): number {
  if (jm <= 6) return 31;
  if (jm <= 11) return 30;
  return jalCal(jy).leap === 0 ? 30 : 29;
}

export function isValidJalaliDate(jy: number, jm: number, jd: number): boolean {
  if (!Number.isInteger(jy) || !Number.isInteger(jm) || !Number.isInteger(jd)) return false;
  if (jy < -61 || jy > 3177 || jm < 1 || jm > 12 || jd < 1) return false;
  try { return jd <= jalaliMonthLength(jy, jm); } catch { return false; }
}

export function parseJalaliDate(raw: unknown): JalaliDateParts | undefined {
  const match = /^(\d{4})\/(\d{1,2})\/(\d{1,2})$/.exec(toEnglishNumbers(raw).trim());
  if (!match) return undefined;
  const jy = Number(match[1]);
  const jm = Number(match[2]);
  const jd = Number(match[3]);
  return isValidJalaliDate(jy, jm, jd) ? { jy, jm, jd } : undefined;
}

export function formatJalaliDate(parts: JalaliDateParts): string {
  return `${String(parts.jy).padStart(4, '0')}/${String(parts.jm).padStart(2, '0')}/${String(parts.jd).padStart(2, '0')}`;
}

export function todayJalali(): JalaliDateParts {
  const now = new Date();
  return toJalali(now.getFullYear(), now.getMonth() + 1, now.getDate());
}

/** Saturday=0 ... Friday=6, suitable for Persian calendar grids. */
export function jalaliWeekdayIndex(jy: number, jm: number, jd: number): number {
  const g = toGregorian(jy, jm, jd);
  const day = new Date(Date.UTC(g.gy, g.gm - 1, g.gd)).getUTCDay();
  return (day + 1) % 7;
}

export function isValidPersianTime(raw: unknown): boolean {
  return /^(?:[01]\d|2[0-3]):[0-5]\d(?::[0-5]\d)?$/.test(toEnglishNumbers(raw).trim());
}

export function isValidJalaliDateTime(raw: unknown): boolean {
  const text = toEnglishNumbers(raw).trim();
  const match = /^(\d{4}\/\d{1,2}\/\d{1,2})\s+((?:[01]\d|2[0-3]):[0-5]\d(?::[0-5]\d)?)$/.exec(text);
  return Boolean(match && parseJalaliDate(match[1]) && isValidPersianTime(match[2]));
}


export function gregorianMonthLength(gy: number, gm: number): number {
  if (!Number.isInteger(gy) || !Number.isInteger(gm) || gm < 1 || gm > 12) return 0;
  return new Date(Date.UTC(gy, gm, 0)).getUTCDate();
}

export function isValidGregorianDate(gy: number, gm: number, gd: number): boolean {
  if (!Number.isInteger(gy) || !Number.isInteger(gm) || !Number.isInteger(gd)) return false;
  if (gy < 1 || gy > 9999 || gm < 1 || gm > 12 || gd < 1) return false;
  return gd <= gregorianMonthLength(gy, gm);
}

export function parseGregorianDate(raw: unknown): GregorianDateParts | undefined {
  const match = /^(\d{4})[\/\-](\d{1,2})[\/\-](\d{1,2})$/.exec(toEnglishNumbers(raw).trim());
  if (!match) return undefined;
  const gy = Number(match[1]);
  const gm = Number(match[2]);
  const gd = Number(match[3]);
  return isValidGregorianDate(gy, gm, gd) ? { gy, gm, gd } : undefined;
}

export function formatGregorianDate(parts: GregorianDateParts): string {
  return `${String(parts.gy).padStart(4, '0')}/${String(parts.gm).padStart(2, '0')}/${String(parts.gd).padStart(2, '0')}`;
}

export function todayGregorian(): GregorianDateParts {
  const now = new Date();
  return { gy: now.getFullYear(), gm: now.getMonth() + 1, gd: now.getDate() };
}

/** Sunday=0 ... Saturday=6, suitable for an English Gregorian grid. */
export function gregorianWeekdayIndex(gy: number, gm: number, gd: number): number {
  return new Date(Date.UTC(gy, gm - 1, gd)).getUTCDay();
}
